/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { addActionBreadcrumb } from './helpers';

export const QUESTION_TYPES = {
  INPUT: 'input',
  MULTI_SELECT: 'multi-select',
  SINGLE_SELECT: 'single-select',
  INPUT_TIME_TIMEZONE: 'input-time-timezone',
};

export type QuestionType = typeof QUESTION_TYPES[keyof typeof QUESTION_TYPES];

export interface Option {
  option: string;
  index: number;
  isSelected: boolean;
}

export interface InputTimeTimezoneAnswer {
  timezone: string | null;
  utcOffset: number | null;
  utcHour: number | null;
  localHour: number | null;
  isAnswerValid: boolean;
}

export interface Question {
  type: QuestionType;
  options?: string[];
  isAnswered?: boolean;
  answer?: any;
  text?: string;
}

export interface SurveyStep {
  questions: Question[];
  name: string;
  isCompleted: boolean;
  currentIndex?: number;
}

export interface SurveyStepWithIndex extends SurveyStep {
  currentIndex: number;
}

export interface Track {
  id: string;
  onboarding: {
    survey: SurveyStep[];
    slack: {
      isCompleted: boolean;
    };
  };
}

export interface OnboardingTask {
  task: string;
  track: Track;
}

export interface OnboardingState {
  track: {
    trackId: string;
    survey: SurveyStepWithIndex[];
    slack: {
      isCompleted: boolean;
    };
  };
}

// Dummy initial state to satisfy TypeScript - this will be overwritten immediately
const dummyInitialState: OnboardingState = {
  track: {
    trackId: '',
    survey: [],
    slack: {
      isCompleted: false,
    },
  },
};

export const initialStateQuestionTypeAnswer = (question: Question): any => {
  switch (question.type) {
    case QUESTION_TYPES.INPUT:
      return '';
    case QUESTION_TYPES.MULTI_SELECT:
      return question.options?.map((option: string, index: number) => ({
        option,
        index,
        isSelected: false,
      }));
    case QUESTION_TYPES.SINGLE_SELECT:
      return question.options?.map((option: string, index: number) => ({
        option,
        index,
        isSelected: false,
      }));
    case QUESTION_TYPES.INPUT_TIME_TIMEZONE:
      return {
        timezone: null,
        utcOffset: null,
        utcHour: null,
        localHour: null,
        isAnswerValid: false,
      };
    default:
      return null;
  }
};

const initialOnboardingState = (
  onboardingTask: OnboardingTask
): OnboardingState => {
  if (onboardingTask.task !== 'track-onboarding') {
    throw new Error('Invalid onboarding task type');
  }

  return {
    track: {
      trackId: onboardingTask.track.id,
      survey: onboardingTask.track.onboarding.survey.map(
        ({
          questions,
          name,
          isCompleted,
        }: SurveyStep): SurveyStepWithIndex => ({
          isCompleted,
          currentIndex: 0,
          name,
          questions: questions.map((question: Question) => ({
            ...question,
            isAnswered: isCompleted,
            answer: initialStateQuestionTypeAnswer(question),
          })),
        })
      ),
      slack: {
        isCompleted: onboardingTask.track.onboarding.slack.isCompleted,
      },
    },
  };
};

export type OnboardingContextType = [
  OnboardingState,
  React.Dispatch<OnboardingAction>
];

// Create the context with a default value that will be replaced
// This tells TypeScript that the context will always have a value when used
export const OnboardingContext = React.createContext<OnboardingContextType>([
  dummyInitialState,
  () => {
    throw new Error('OnboardingContext used outside of provider');
  },
]);
OnboardingContext.displayName = 'OnboardingContext';

export const ONBOARDING_ACTION = {
  TRACK: {
    SUBMIT_SURVEY_STEP: 'TRACK_SUBMIT_SURVEY_STEP',
    CONFIRM_JOIN_SLACK: 'TRACK_CONFIRM_JOIN_SLACK',
    ANSWER_SURVEY_QUESTION: 'TRACK_ANSWER_QUESTION_QUESTION',
    INCREMENT_SURVEY_STEP: 'TRACK_INCREMENT_SURVEY_STEP',
    DECREMENT_SURVEY_STEP: 'TRACK_DECREMENT_SURVEY_STEP',
  },
};

export type OnboardingActionType =
  typeof ONBOARDING_ACTION.TRACK[keyof typeof ONBOARDING_ACTION.TRACK];

export interface OnboardingAction {
  type: OnboardingActionType;
  payload?: {
    stepIndex?: number;
    questionIndex?: number;
    answer?: any;
  };
}

function onboardingReducer(
  state: OnboardingState,
  action: OnboardingAction
): OnboardingState {
  addActionBreadcrumb({ context: 'onboarding', actionType: action.type });
  switch (action.type) {
    case ONBOARDING_ACTION.TRACK.SUBMIT_SURVEY_STEP: {
      if (!action.payload || action.payload.stepIndex === undefined) {
        throw new Error('stepIndex is required for SUBMIT_SURVEY_STEP');
      }

      return {
        ...state,
        track: {
          ...state.track,
          survey: state.track.survey.map((step, index) =>
            index !== action.payload?.stepIndex
              ? step
              : {
                  ...step,
                  isCompleted: true,
                }
          ),
        },
      };
    }
    case ONBOARDING_ACTION.TRACK.CONFIRM_JOIN_SLACK: {
      return {
        ...state,
        track: {
          ...state.track,
          slack: {
            ...state.track.slack,
            isCompleted: true,
          },
        },
      };
    }
    case ONBOARDING_ACTION.TRACK.INCREMENT_SURVEY_STEP: {
      const surveyStepIndexIncrement = action.payload?.stepIndex;
      if (surveyStepIndexIncrement === undefined) {
        throw new Error('stepIndex is required for INCREMENT_SURVEY_STEP');
      }

      if (
        state.track.survey[surveyStepIndexIncrement].questions.length <=
        state.track.survey[surveyStepIndexIncrement].currentIndex + 1
      ) {
        throw Error('Cannot increment survey step after last question');
      }

      return {
        ...state,
        track: {
          ...state.track,
          survey: state.track.survey.map((step, index) =>
            surveyStepIndexIncrement !== index
              ? step
              : {
                  ...step,
                  currentIndex: step.currentIndex + 1,
                }
          ),
        },
      };
    }
    case ONBOARDING_ACTION.TRACK.DECREMENT_SURVEY_STEP: {
      const surveyStepIndexDecrement = action.payload?.stepIndex;
      if (surveyStepIndexDecrement === undefined) {
        throw new Error('stepIndex is required for DECREMENT_SURVEY_STEP');
      }

      if (state.track.survey[surveyStepIndexDecrement].currentIndex === 0) {
        throw Error('Cannot decrement survey step on first question');
      }

      return {
        ...state,
        track: {
          ...state.track,
          survey: state.track.survey.map((step, index) =>
            surveyStepIndexDecrement !== index
              ? step
              : {
                  ...step,
                  currentIndex: step.currentIndex - 1,
                }
          ),
        },
      };
    }
    case ONBOARDING_ACTION.TRACK.ANSWER_SURVEY_QUESTION: {
      const surveyStepIndexAnswer = action.payload?.stepIndex;
      const questionIndex = action.payload?.questionIndex;
      const answer = action.payload?.answer;

      if (surveyStepIndexAnswer === undefined) {
        throw new Error('stepIndex is required for ANSWER_SURVEY_QUESTION');
      }

      if (questionIndex === undefined) {
        throw new Error('questionIndex is required for ANSWER_SURVEY_QUESTION');
      }

      if (answer === undefined) {
        throw new Error('answer is required for ANSWER_SURVEY_QUESTION');
      }

      return {
        ...state,
        track: {
          ...state.track,
          survey: state.track.survey.map((step, index) =>
            surveyStepIndexAnswer !== index
              ? step
              : {
                  ...step,
                  questions: step.questions.map((question, idx) =>
                    idx === questionIndex
                      ? {
                          ...question,
                          answer,
                          isAnswered: isQuestionAnswered({
                            type: question.type,
                            answer,
                          }),
                        }
                      : question
                  ),
                }
          ),
        },
      };
    }
    default:
      throw Error(`Invalid action type "${action.type}"`);
  }
}

interface QuestionAnsweredProps {
  type: QuestionType;
  answer: any;
}

function isQuestionAnswered({ type, answer }: QuestionAnsweredProps): boolean {
  switch (type) {
    case QUESTION_TYPES.INPUT:
      return !!answer.trim();
    case QUESTION_TYPES.MULTI_SELECT:
      return answer.filter((item: Option) => item.isSelected).length > 0;
    case QUESTION_TYPES.SINGLE_SELECT:
      return answer.filter((item: Option) => item.isSelected).length > 0;
    case QUESTION_TYPES.INPUT_TIME_TIMEZONE: {
      const { localHour, isAnswerValid } = answer as InputTimeTimezoneAnswer;
      return (
        typeof localHour === 'number' &&
        Number.isFinite(localHour) &&
        !Number.isNaN(localHour) &&
        isAnswerValid
      );
    }
    default:
      throw Error(`Invalid question type "${type}"`);
  }
}

interface OnboardingProviderProps {
  onboardingTask: OnboardingTask;
  children: React.ReactNode;
}

export function OnboardingProvider({
  onboardingTask,
  children,
}: OnboardingProviderProps) {
  const initialState = initialOnboardingState(onboardingTask);

  const [state, dispatch] = React.useReducer(onboardingReducer, initialState);

  // Using React.useMemo to avoid unnecessary re-renders
  const contextValue = React.useMemo(
    () => [state, dispatch] as OnboardingContextType,
    [state, dispatch]
  );

  return (
    <OnboardingContext.Provider value={contextValue}>
      {children}
    </OnboardingContext.Provider>
  );
}
